/**
 * Translation for parent-facing surfaces.
 *
 * Hand-rolled rather than react-i18next, for one reason that outweighs the
 * convenience: `ml.ts` is typed against `en.ts`, so a string added without a
 * translation is a COMPILE ERROR. The owner's standing rule is that anything a
 * parent comes across must be translated, including work done months from now,
 * and a build failure enforces that where a convention would not. react-i18next
 * resolves keys at runtime and would let the same mistake ship.
 *
 * What is deliberately NOT here: namespaces, lazy-loaded bundles, a translation
 * management backend. Two locales and ~50 keys do not need them, and `Intl`
 * already provides plural rules and formatting.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuthStore } from '../auth-store';
import { setFormatLocale } from '../format';
import { en, type TranslationKey } from './en';
import { ml } from './ml';

export type Locale = 'en' | 'ml';

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { en, ml };

/**
 * One `Intl.PluralRules` per locale, not one per call.
 *
 * `lib/format.ts` caches its `Intl` formatters for exactly this reason and this
 * file was re-creating the cost it avoids — plural keys render inside list rows,
 * so the construction was on the hot path.
 */
const PLURAL_RULES = new Map<Locale, Intl.PluralRules>();
const pluralRules = (locale: Locale): Intl.PluralRules => {
  const existing = PLURAL_RULES.get(locale);
  if (existing !== undefined) return existing;
  const made = new Intl.PluralRules(locale);
  PLURAL_RULES.set(locale, made);
  return made;
};

/** Interpolation placeholders. Hoisted: a literal rebuilds the RegExp per call. */
const PLACEHOLDER = /\{([^}\s]+)\}/gu;

/** Values interpolated into a string. `count` also selects the plural form. */
export type TranslationValues = Record<string, string | number>;

/**
 * Values for a plural base key. `count` is REQUIRED and must be a number.
 *
 * Both halves matter. `t('family.childCount')` with no values used to compile
 * and render the literal text "family.childCount" to a parent, because a plural
 * base exists in neither dictionary — only its `_one`/`_other` variants do — so
 * every fallback missed. And `{ count: '2' }` typechecked under the old
 * `string | number` signature while `typeof count === 'number'` rejected it at
 * runtime, taking the same path to the same raw key.
 */
export type PluralValues = TranslationValues & { count: number };

/**
 * A plural pair `foo_one` / `foo_other` is CALLED as `foo`, so the base name
 * has to be a valid argument even though no such entry exists in the
 * dictionaries. Derived from the keys rather than hand-listed, so adding a
 * plural pair makes its base callable automatically — and a typo in the base
 * still fails to compile.
 */
type PluralBase<K extends string> = K extends `${infer Base}_one` ? Base : never;

/** Everything `t()` accepts: a literal key, or the base of a plural pair. */
export type TranslatableKey = TranslationKey | PluralBase<TranslationKey>;

/**
 * Overloaded so the compiler enforces what the runtime needs: a plural base
 * cannot be called without a numeric `count`, and a literal key keeps its
 * optional values.
 */
export interface Translate {
  (key: PluralBase<TranslationKey>, values: PluralValues): string;
  (key: TranslationKey, values?: TranslationValues): string;
}

interface I18nContextValue {
  locale: Locale;
  t: Translate;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Resolve a key, applying plural selection then interpolation.
 *
 * Plural pairs are `key_one` / `key_other`. `Intl.PluralRules` decides which,
 * rather than a hardcoded `count === 1`: Malayalam happens to share English's
 * one/other split, but the whole point of using the platform rule is that the
 * next locale added does not silently get English's grammar.
 */
const resolve = (
  locale: Locale,
  key: TranslatableKey,
  values: TranslationValues | undefined,
): string => {
  const dict = DICTIONARIES[locale];
  const lookup = (k: string): string | undefined =>
    (dict as Record<string, string | undefined>)[k];
  let template: string | undefined;

  if (values !== undefined && typeof values.count === 'number') {
    // `Intl.PluralRules`, not `count === 1`. Malayalam happens to share
    // English's one/other split, but the point of using the platform rule is
    // that the next locale added does not silently inherit English grammar.
    const category = pluralRules(locale).select(values.count);
    template = lookup(`${key}_${category}`) ?? lookup(`${key}_other`);
  }
  template ??= lookup(key);

  // Fall back to English rather than showing a parent a raw key. Reachable only
  // if a plural variant is missing, since `ml` is a total Record over the
  // literal keys.
  template ??= (en as Record<string, string | undefined>)[key];
  if (template === undefined) {
    return key;
  }

  if (values === undefined) return template;
  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = values[name];
    return value === undefined ? whole : String(value);
  });
};

export function I18nProvider({ children }: { children: ReactNode }) {
  // The locale lives on the user record, not in browser storage: the sends that
  // most need translating (fee reminders, attendance alerts) originate in the
  // Worker with no browser attached, so a localStorage-only preference would be
  // unreachable exactly when it matters. See migration 0028.
  const locale: Locale = useAuthStore((s) => s.user?.locale ?? 'en');

  // Set DURING render, not in an effect.
  //
  // Effects run after the commit, so an effect here would leave the formatters
  // holding the previous locale for exactly the paint that the locale change
  // was supposed to fix — and mutating a module variable schedules no
  // re-render, so nothing would correct it. The children of this provider are a
  // stable element, so React bails out of that subtree anyway.
  //
  // A side effect in render is normally wrong; this one is an idempotent write
  // to a module-level formatter cache keyed by locale, not a subscription or a
  // state update, so re-running it is free and running it early is the point.
  setFormatLocale(locale);

  useEffect(() => {
    // `lang` is not cosmetic: screen readers pick their voice from it, and
    // without it a Malayalam page is read aloud with English pronunciation.
    document.documentElement.setAttribute('lang', locale);
  }, [locale]);

  const t = useCallback<Translate>(
    (key, values) => resolve(locale, key, values),
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, t }), [locale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Translate within a component. Throws if the provider is missing. */
export const useT = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useT must be used inside <I18nProvider>');
  }
  return ctx;
};

export type { TranslationKey };
